import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Icon,
  Text,
  Card,
  CardBody,
  Spinner,
  Alert,
  AlertIcon,
  Checkbox,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiRefreshCw,
  FiFilter,
  FiDownload,
} from 'react-icons/fi';
import { canvasAPI } from '../../../services/api';

const AllCertificatesTab = ({ certificates, loading, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('');
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [isDownloadingMultiple, setIsDownloadingMultiple] = useState(false);
  const [isDownloading, setIsDownloading] = useState(null);
  
  const toast = useToast();

  // Descargar certificado individual
  const handleDownload = async (certificateId, studentName) => {
    try {
      setIsDownloading(certificateId);
      await canvasAPI.downloadCertificateByAdmin(
        certificateId,
        `certificado_${studentName.replace(/\s+/g, '_')}`
      );
      
      toast({
        title: 'Certificado descargado',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast({
        title: 'Error',
        description: 'No se pudo descargar el certificado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(null);
    }
  };

  // Obtener lista única de cursos
  const courses = useMemo(() => {
    const uniqueCourses = [...new Set(certificates.map(c => c.context_title))];
    return uniqueCourses.sort();
  }, [certificates]);

  // Obtener lista única de plantillas/tipos
  const templates = useMemo(() => {
    const templateSet = new Set();
    certificates.forEach(cert => {
      if (cert.template_name) {
        templateSet.add(cert.template_name);
      }
    });
    return Array.from(templateSet).sort();
  }, [certificates]);

  // Filtrar certificados
  const filteredCertificates = useMemo(() => {
    return certificates.filter(cert => {
      // Búsqueda por nombre/ID
      const matchesSearch = searchQuery === '' || 
        cert.student_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cert.certificate_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cert.course_name.toLowerCase().includes(searchQuery.toLowerCase());

      // Filtro por curso
      const matchesCourse = courseFilter === '' || cert.context_title === courseFilter;

      // Filtro por estado
      const matchesStatus = statusFilter === 'all' || cert.status === statusFilter;

      // Filtro por plantilla/tipo
      const matchesTemplate = templateFilter === '' || 
        (templateFilter === 'default' ? !cert.template_name : cert.template_name === templateFilter);

      return matchesSearch && matchesCourse && matchesStatus && matchesTemplate;
    });
  }, [certificates, searchQuery, courseFilter, statusFilter, templateFilter]);

  // Certificados elegibles para descarga (emitidos y con PDF generado)
  const downloadableCertificates = useMemo(() => {
    return filteredCertificates.filter(
      cert => cert.pdf_generated && cert.status === 'issued'
    );
  }, [filteredCertificates]);

  // Manejar selección individual
  const handleSelectCertificate = (certId) => {
    setSelectedCertificates(prev => {
      if (prev.includes(certId)) {
        return prev.filter(id => id !== certId);
      } else {
        return [...prev, certId];
      }
    });
  };

  // Manejar selección de todos
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const downloadableIds = downloadableCertificates.map(cert => cert.id);
      setSelectedCertificates(downloadableIds);
    } else {
      setSelectedCertificates([]);
    }
  };

  // Descargar múltiples certificados
  const handleDownloadMultiple = async () => {
    if (selectedCertificates.length === 0) return;

    try {
      setIsDownloadingMultiple(true);
      
      // El backend genera automáticamente el nombre con timestamp
      await canvasAPI.downloadMultipleCertificatesByAdmin(selectedCertificates);
      
      toast({
        title: 'Descarga completada',
        description: `${selectedCertificates.length} certificado(s) descargado(s) exitosamente`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      
      // Limpiar selección
      setSelectedCertificates([]);
    } catch (error) {
      console.error('Error downloading multiple certificates:', error);
      toast({
        title: 'Error en descarga múltiple',
        description: error.response?.data?.detail || 'No se pudieron descargar los certificados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingMultiple(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      issued: { color: 'green', label: 'Emitido' },
      revoked: { color: 'red', label: 'Revocado' },
      pending: { color: 'yellow', label: 'Pendiente' },
    };

    const config = statusConfig[status] || { color: 'gray', label: status };
    return <Badge colorScheme={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color="gray.600">Cargando certificados...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Card mb={6}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {/* Actions */}
            <HStack spacing={3} flexWrap="wrap">
              <Button
                leftIcon={<FiRefreshCw />}
                onClick={onRefresh}
                variant="outline"
                colorScheme="blue"
              >
                Actualizar
              </Button>
              {selectedCertificates.length > 0 && (
                <Button
                  leftIcon={<FiDownload />}
                  onClick={handleDownloadMultiple}
                  colorScheme="green"
                  isLoading={isDownloadingMultiple}
                  loadingText="Descargando..."
                >
                  Descargar Seleccionados ({selectedCertificates.length})
                </Button>
              )}
              <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                Total: {certificates.length}
              </Badge>
              <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                Filtrados: {filteredCertificates.length}
              </Badge>
              {downloadableCertificates.length > 0 && (
                <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
                  Disponibles: {downloadableCertificates.length}
                </Badge>
              )}
            </HStack>

            {/* Filters */}
            <HStack spacing={3} flexWrap="wrap" align="end">
              <Icon as={FiFilter} color="gray.500" boxSize={5} />
              
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>

              <Select
                maxW="250px"
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                placeholder="Todos los cursos"
              >
                {courses.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </Select>

              <Select
                maxW="200px"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Todos los estados</option>
                <option value="issued">Emitidos</option>
                <option value="revoked">Revocados</option>
              </Select>

              <Select
                maxW="250px"
                value={templateFilter}
                onChange={(e) => setTemplateFilter(e.target.value)}
                placeholder="Todos los tipos"
              >
                {templates.length > 0 && certificates.some(c => !c.template_name) && (
                  <option value="default">Predeterminado</option>
                )}
                {templates.map((template) => (
                  <option key={template} value={template}>
                    {template}
                  </option>
                ))}
              </Select>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Certificates Table */}
      {filteredCertificates.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          {certificates.length === 0 
            ? 'No hay certificados en el sistema' 
            : 'No se encontraron certificados con los filtros aplicados'}
        </Alert>
      ) : (
        <Card>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th width="40px">
                    <Checkbox
                      isChecked={
                        downloadableCertificates.length > 0 &&
                        selectedCertificates.length === downloadableCertificates.length
                      }
                      isIndeterminate={
                        selectedCertificates.length > 0 &&
                        selectedCertificates.length < downloadableCertificates.length
                      }
                      onChange={handleSelectAll}
                      isDisabled={downloadableCertificates.length === 0}
                    />
                  </Th>
                  <Th>ID</Th>
                  <Th>Estudiante</Th>
                  <Th>Curso</Th>
                  <Th>Tipo</Th>
                  <Th>Context</Th>
                  <Th isNumeric>Calificación</Th>
                  <Th>Fecha Emisión</Th>
                  <Th>Estado</Th>
                  <Th>Emitido por</Th>
                  <Th>Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredCertificates.map((cert) => {
                  const isDownloadable = cert.pdf_generated && cert.status === 'issued';
                  return (
                  <Tr key={cert.id}>
                    <Td>
                      <Checkbox
                        isChecked={selectedCertificates.includes(cert.id)}
                        onChange={() => handleSelectCertificate(cert.id)}
                        isDisabled={!isDownloadable}
                      />
                    </Td>
                    <Td>
                      <Text fontSize="xs" fontFamily="mono" fontWeight="medium">
                        {cert.certificate_id}
                      </Text>
                    </Td>
                    <Td fontWeight="medium">{cert.student_name}</Td>
                    <Td>
                      <Text fontSize="sm">{cert.course_name}</Text>
                    </Td>
                    <Td>
                      {cert.template_name ? (
                        <Badge colorScheme="purple" fontSize="xs">
                          {cert.template_name}
                        </Badge>
                      ) : (
                        <Text fontSize="xs" color="gray.400">Predeterminado</Text>
                      )}
                    </Td>
                    <Td>
                      <Text fontSize="xs" color="gray.600">
                        {cert.context_title}
                      </Text>
                    </Td>
                    <Td isNumeric>
                      {cert.final_grade ? (
                        <Badge colorScheme="green" fontSize="xs">
                          {cert.final_grade.toFixed(1)}
                        </Badge>
                      ) : (
                        <Text fontSize="xs" color="gray.500">N/A</Text>
                      )}
                    </Td>
                    <Td fontSize="xs">
                      {new Date(cert.issue_date).toLocaleDateString('es-ES')}
                    </Td>
                    <Td>{getStatusBadge(cert.status)}</Td>
                    <Td fontSize="xs" color="gray.600">
                      {cert.issued_by}
                    </Td>
                    <Td>
                      {isDownloadable && (
                        <IconButton
                          icon={<FiDownload />}
                          aria-label="Descargar"
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleDownload(cert.id, cert.student_name)}
                          isLoading={isDownloading === cert.id}
                        />
                      )}
                    </Td>
                  </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default AllCertificatesTab;
